import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { fmtFecha } from "../lib/format";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const TipoCambio = ({ tc, onSaved }) => {
  const [v, setV] = useState(tc.valor);
  const save = async () => {
    try {
      await api.put("/company/tipo-cambio", { valor: v });
      toast.success("Tipo de cambio actualizado; se recalcula el riesgo");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2 lg:col-span-2" data-testid="dash-tipo-cambio">
      <p className="text-xs uppercase tracking-wider text-slate-500">Tipo de cambio (dólar observado del Banco Central)</p>
      {tc.desactualizado && <p className="text-sm text-rose-700 font-semibold" data-testid="dash-tipo-cambio-alert">El tipo de cambio tiene más de 7 días sin actualizar.</p>}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Input className="w-28 h-9" value={v} onChange={(e) => setV(e.target.value)} data-testid="dash-tipo-cambio-input" /> CLP por USD
        <Button size="sm" onClick={save} disabled={String(v) === String(tc.valor)} data-testid="dash-tipo-cambio-save">Actualizar</Button>
        <span className="text-slate-500">Última actualización: {fmtFecha(tc.fecha)} · {tc.por}</span>
      </div>
    </div>
  );
};

const Box = ({ title, children, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-2" data-testid={testId}>
    <p className="text-xs uppercase tracking-wider text-slate-500">{title}</p>
    {children}
  </div>
);

const Pairs = ({ obj }) => (
  <div className="flex flex-wrap gap-4">{Object.entries(obj).map(([k, v]) => <p key={k} className="text-sm"><b className="text-2xl font-heading">{v}</b> <span className="text-slate-600">{k}</span></p>)}</div>
);

export const DashboardStats = () => {
  const [d, setD] = useState(null);
  const [alertas, setAlertas] = useState([]);
  const [k, setK] = useState(0);
  useEffect(() => {
    api.get("/company/dashboard").then((r) => setD(r.data)).catch(() => setD(null));
    api.get("/listas/estado").then((r) => setAlertas(r.data.alertas)).catch(() => {});
  }, [k]);
  if (!d) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="dashboard-stats">
      {d.correo_modo === "simulado" && <p className="lg:col-span-2 text-sm text-amber-700" data-testid="dash-correo-simulado">Correos en modo SIMULADO: no se envían realmente hasta configurar el proveedor de correo.</p>}
      {!d.didit_configurado && <p className="lg:col-span-2 text-sm text-amber-700" data-testid="dash-didit-no-configurado">Didit no está configurado: las verificaciones usan el motor propio (solo documento, sin selfie) y quedan siempre en revisión.</p>}
      <div className={`lg:col-span-2 rounded-xl border p-4 ${d.provisionales.mas_de_48h ? "border-rose-300 bg-rose-50" : "bg-white"}`} data-testid="dash-provisionales">
        <p className="font-semibold">Clientes con aprobación provisional esperando revisión: {d.provisionales.total}</p>
        {d.provisionales.desde && <p className="text-sm text-slate-600">El más antiguo desde {new Date(d.provisionales.desde).toLocaleString("es-CL", { timeZone: "America/Santiago" })}</p>}
        {d.provisionales.mas_de_48h > 0 && <p className="text-sm text-rose-700 font-semibold" data-testid="dash-provisionales-48h">{d.provisionales.mas_de_48h} llevan más de 48 horas sin revisión</p>}
      </div>
      <TipoCambio key={d.tipo_cambio.fecha} tc={d.tipo_cambio} onSaved={() => setK(k + 1)} />
      {alertas.length > 0 && <p className="lg:col-span-2 text-sm text-rose-700" data-testid="dashboard-lists-alert">Listas desactualizadas: {alertas.join(" · ")}</p>}
      <Box title="Clientes por nivel de riesgo" testId="dash-riesgo"><Pairs obj={d.por_nivel} /></Box>
      <Box title="Clientes por estado" testId="dash-estado"><Pairs obj={d.por_estado} /></Box>
      <Box title="Casos abiertos por antigüedad" testId="dash-casos"><Pairs obj={d.casos_abiertos_por_antiguedad} /></Box>
      <Box title="Fichas por vencer en 30 días" testId="dash-vencer">
        {d.por_vencer_30_dias.map((c) => <p key={c.id} className="text-sm"><Link className="underline" to={`/panel/clientes/${c.id}`}>{c.nombres} {c.apellido_paterno}</Link> · actualizada {fmtFecha(c.actualizado_en)}</p>)}
        {!d.por_vencer_30_dias.length && <p className="text-sm text-slate-500">Ninguna.</p>}
      </Box>
      <Box title="Listas, últimos 30 días (para calibrar)" testId="dash-metricas">
        <table className="text-sm w-full"><thead><tr className="text-left text-slate-500"><th>Fuente</th><th>Casos abiertos</th><th>Homónimos descartados</th><th>Confirmados</th></tr></thead>
          <tbody>{Object.entries(d.metricas_listas_30_dias).map(([f, m]) => <tr key={f} className="border-t" data-testid={`dash-metric-${f}`}><td className="uppercase">{f}</td><td>{m.abiertos}</td><td>{m.homonimos}</td><td>{m.confirmados}</td></tr>)}</tbody>
        </table>
      </Box>
    </div>
  );
};
